/* ============================================================
   MIMOSA ROYALE — LUXURY ANIMATION ENGINE
   Runs after main.js. GPU-only. Respects prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGSAP = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

  /* ──────────────────────────────────────────────────────────
     1. LUX REVEAL — IntersectionObserver for [data-lux] attrs
     More sophisticated than the base data-reveal system.
  ─────────────────────────────────────────────────────────── */
  const luxObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const el = e.target;
      const delay = parseFloat(el.dataset.luxDelay || 0);
      setTimeout(() => el.classList.add('lux-in'), delay * 1000);
      luxObserver.unobserve(el);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -48px 0px' });

  document.querySelectorAll('[data-lux]').forEach(el => luxObserver.observe(el));

  /* ──────────────────────────────────────────────────────────
     2. LUX STAGGER — [data-lux-stagger] parent staggers children
  ─────────────────────────────────────────────────────────── */
  const luxStaggerObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const children = [...e.target.children];
      const base = parseFloat(e.target.dataset.luxStaggerBase || 0.08);
      children.forEach((child, i) => {
        if (!child.dataset.lux) child.dataset.lux = 'up';
        setTimeout(() => child.classList.add('lux-in'), i * base * 1000);
      });
      luxStaggerObserver.unobserve(e.target);
    });
  }, { threshold: 0.06, rootMargin: '0px 0px -32px 0px' });

  document.querySelectorAll('[data-lux-stagger]').forEach(el => {
    [...el.children].forEach(child => {
      if (!child.dataset.lux) child.dataset.lux = 'up';
    });
    luxStaggerObserver.observe(el);
  });

  /* ──────────────────────────────────────────────────────────
     3. HERO ENTRANCE SEQUENCE — GSAP timeline
     Fires once on load for the primary hero section.
  ─────────────────────────────────────────────────────────── */
  if (hasGSAP && !prefersReduced) {
    const hero = document.querySelector('section.hero, .hero-section');
    if (hero) {
      // NOTE: Header and announcement bar are NOT animated — they must always
      // be visible immediately. Only animate content inside the hero section.

      const tl = gsap.timeline({ delay: 0.05 });

      // Hero eyebrow label
      const heroEyebrow = hero.querySelector('.hero-eyebrow, .section-label');
      if (heroEyebrow) {
        tl.fromTo(heroEyebrow,
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
          0
        );
      }

      // Hero title — animate as a block (safe, no innerHTML mangling)
      const heroTitle = hero.querySelector('h1, .hero-title');
      if (heroTitle) {
        tl.fromTo(heroTitle,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.0, ease: 'power4.out' },
          0.15
        );
      }

      // Hero subtitle
      const heroSub = hero.querySelector('.hero-subtitle, .hero-sub');
      if (heroSub) {
        tl.fromTo(heroSub,
          { y: 24, opacity: 0, filter: 'blur(4px)' },
          { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.85, ease: 'power3.out' },
          0.4
        );
      }

      // Hero CTAs
      const heroCtas = hero.querySelectorAll('.hero-ctas .btn, .hero-ctas a');
      if (heroCtas.length) {
        tl.fromTo(heroCtas,
          { y: 20, opacity: 0, scale: 0.96 },
          { y: 0, opacity: 1, scale: 1, duration: 0.65, ease: 'back.out(1.4)', stagger: 0.1 },
          0.6
        );
      }

      // Hero stats
      const heroStats = hero.querySelectorAll('.hero-stat');
      if (heroStats.length) {
        tl.fromTo(heroStats,
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.07 },
          0.75
        );
      }

      // Hero image — subtle scale in
      const heroImg = hero.querySelector('.hero-products-image, .hero-products img');
      if (heroImg) {
        gsap.fromTo(heroImg,
          { scale: 1.06, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1.4, ease: 'power3.out', delay: 0.1 }
        );
      }
    }
  }

  /* ──────────────────────────────────────────────────────────
     4. PARALLAX — subtle scroll-linked depth
     Only on desktop, only GPU transforms.
  ─────────────────────────────────────────────────────────── */
  if (hasGSAP && !prefersReduced && window.innerWidth > 768) {
    // Hero image parallax
    const heroImg = document.querySelector('.hero-section img, .hero-cans, .hero-product-img');
    if (heroImg) {
      gsap.to(heroImg, {
        yPercent: 18,
        ease: 'none',
        scrollTrigger: {
          trigger: heroImg.closest('section') || heroImg,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5
        }
      });
    }

    // Section background layers — [data-parallax="N"] where N = speed 0.1–0.5
    document.querySelectorAll('[data-parallax]').forEach(el => {
      const speed = parseFloat(el.dataset.parallax || 0.2);
      gsap.to(el, {
        yPercent: speed * 60,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('section') || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1
        }
      });
    });
  }

  /* ──────────────────────────────────────────────────────────
     5. MAGNETIC BUTTONS — CTA buttons subtly follow cursor
  ─────────────────────────────────────────────────────────── */
  if (!prefersReduced && window.innerWidth > 768) {
    document.querySelectorAll('.btn-primary, [data-magnetic]').forEach(btn => {
      const strength = parseFloat(btn.dataset.magneticStrength || 0.3);

      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) * strength;
        const dy = (e.clientY - cy) * strength;
        btn.style.transform = `translate(${dx}px, ${dy}px)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ──────────────────────────────────────────────────────────
     6. FLOATING PRODUCT IMAGES — auto-apply to product cards
  ─────────────────────────────────────────────────────────── */
  if (!prefersReduced) {
    const productImgs = document.querySelectorAll('.product-card img, .pdp-hero-img, .hero-cans img');
    productImgs.forEach((img, i) => {
      const classes = ['lux-float', 'lux-float-slow', 'lux-float-alt'];
      img.classList.add(classes[i % 3]);
    });
  }

  /* ──────────────────────────────────────────────────────────
     7. GOLD LINE REVEALS — animate .lux-line elements
  ─────────────────────────────────────────────────────────── */
  const lineObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('lux-in');
        lineObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('.lux-line').forEach(el => lineObserver.observe(el));

  /* ──────────────────────────────────────────────────────────
     8. SMOOTH COUNTER ENHANCEMENT — richer easing
     Works alongside main.js counter, targets new selectors.
  ─────────────────────────────────────────────────────────── */
  function luxCountUp(el, duration) {
    if (el.dataset.counted) return;
    el.dataset.counted = '1';
    const raw = el.textContent.trim();
    const num = parseFloat(raw.replace(/[^0-9.]/g, ''));
    const prefix = raw.match(/^[^0-9]*/)[0];
    const suffix = raw.replace(/^[^0-9]*[\d,.]+/, '');
    if (!num) return;

    const start = performance.now();
    const dur = duration || 1800;
    const step = (now) => {
      const p = Math.min((now - start) / dur, 1);
      // Cubic ease out
      const eased = 1 - Math.pow(1 - p, 4);
      const val = Math.round(eased * num);
      el.textContent = prefix + val.toLocaleString() + suffix;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        luxCountUp(e.target);
        counterObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.6 });

  // Target all stat numbers not already handled by main.js
  document.querySelectorAll(
    '.stat-number, .lux-counter, [data-lux-count], ' +
    '.phase-stat-number, .roadmap-stat, .inf-stat-number'
  ).forEach(el => counterObserver.observe(el));

  /* ──────────────────────────────────────────────────────────
     9. BUTTON RIPPLE — tactile click feedback
  ─────────────────────────────────────────────────────────── */
  document.querySelectorAll('.btn-primary, .btn, [data-ripple]').forEach(btn => {
    btn.addEventListener('click', function (e) {
      if (prefersReduced) return;

      const ripple = document.createElement('span');
      const rect = this.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 1.5;
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      Object.assign(ripple.style, {
        position: 'absolute',
        width: size + 'px',
        height: size + 'px',
        top: y + 'px',
        left: x + 'px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.25)',
        transform: 'scale(0)',
        animation: 'rippleExpand 0.55s cubic-bezier(0.16,1,0.3,1) forwards',
        pointerEvents: 'none',
        zIndex: 999
      });

      // Inject keyframes once
      if (!document.getElementById('lux-ripple-style')) {
        const style = document.createElement('style');
        style.id = 'lux-ripple-style';
        style.textContent = '@keyframes rippleExpand { to { transform: scale(1); opacity: 0; } }';
        document.head.appendChild(style);
      }

      const prevPos = this.style.position;
      if (!prevPos || prevPos === 'static') this.style.position = 'relative';
      this.style.overflow = 'hidden';
      this.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    });
  });

  /* ──────────────────────────────────────────────────────────
     10. SCROLL-LINKED SECTION NUMBER COUNTER
         Numbers in award/stat grids count up elegantly
  ─────────────────────────────────────────────────────────── */
  // NOTE: .hero-stat-number / .awards-hero-stat-number are counted by main.js
  // (single source of truth). Observing them here too caused TWO count-up loops
  // to race on the same element — the longer (1800ms) loop finished last and
  // overwrote the value with a target parsed from the other's mid-animation
  // text, sticking the display at a corrupted low number ("1+" instead of "50+").
  // Duplicate observation removed.

  /* ──────────────────────────────────────────────────────────
     11. SECTION HEADING SHIMMER — gold text on .gold class
  ─────────────────────────────────────────────────────────── */
  document.querySelectorAll('.section-title.gold, .hero-title.gold, h1.gold, h2.gold').forEach(el => {
    if (!el.classList.contains('lux-shimmer-text')) {
      el.classList.add('lux-shimmer-text');
    }
  });

  /* ──────────────────────────────────────────────────────────
     12. NAV — Smooth active state on scroll
  ─────────────────────────────────────────────────────────── */
  const sections = document.querySelectorAll('section[id]');
  const navLinks = document.querySelectorAll('.nav-links a[href*="#"]');

  if (sections.length && navLinks.length) {
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const id = e.target.id;
          navLinks.forEach(link => {
            const isActive = link.getAttribute('href').includes(id);
            link.style.color = isActive ? 'var(--gold)' : '';
          });
        }
      });
    }, { threshold: 0.4 });
    sections.forEach(s => sectionObserver.observe(s));
  }

  /* ──────────────────────────────────────────────────────────
     13. GSAP SCROLL-TRIGGERED SECTION REVEALS
         Premium batch reveals for entire sections
  ─────────────────────────────────────────────────────────── */
  if (hasGSAP && !prefersReduced) {
    // Feature bar items
    const featureItems = document.querySelectorAll('.feature-item');
    if (featureItems.length) {
      gsap.fromTo(featureItems,
        { y: 40, opacity: 0, scale: 0.95 },
        {
          y: 0, opacity: 1, scale: 1,
          duration: 0.7,
          ease: 'back.out(1.3)',
          stagger: 0.09,
          scrollTrigger: {
            trigger: featureItems[0].closest('section') || featureItems[0],
            start: 'top 82%',
            once: true
          }
        }
      );
    }

    // Award items grid
    const awardItems = document.querySelectorAll('.award-item:not([data-gsap-done])');
    if (awardItems.length) {
      awardItems.forEach(el => el.dataset.gsapDone = '1');
      gsap.fromTo(awardItems,
        { y: 48, opacity: 0, scale: 0.9 },
        {
          y: 0, opacity: 1, scale: 1,
          duration: 0.8,
          ease: 'back.out(1.4)',
          stagger: 0.1,
          scrollTrigger: {
            trigger: awardItems[0].closest('section') || awardItems[0],
            start: 'top 80%',
            once: true
          }
        }
      );
    }

    // Trust band items
    const trustItems = document.querySelectorAll('.mr-trust-item');
    if (trustItems.length) {
      gsap.fromTo(trustItems,
        { y: 24, opacity: 0 },
        {
          y: 0, opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.1,
          scrollTrigger: {
            trigger: trustItems[0].closest('.mr-trust-band') || trustItems[0],
            start: 'top 90%',
            once: true
          }
        }
      );
    }

    // VIP perks
    const vipPerks = document.querySelectorAll('.vip-perk');
    if (vipPerks.length) {
      gsap.fromTo(vipPerks,
        { y: 36, opacity: 0, scale: 0.93 },
        {
          y: 0, opacity: 1, scale: 1,
          duration: 0.65,
          ease: 'back.out(1.5)',
          stagger: 0.08,
          scrollTrigger: {
            trigger: vipPerks[0].closest('section') || vipPerks[0],
            start: 'top 80%',
            once: true
          }
        }
      );
    }

    // Occasion cards
    const occasionCards = document.querySelectorAll('.occasion-card, [class*="occasion"]');
    if (occasionCards.length) {
      gsap.fromTo(occasionCards,
        { y: 40, opacity: 0, scale: 0.96 },
        {
          y: 0, opacity: 1, scale: 1,
          duration: 0.75,
          ease: 'back.out(1.3)',
          stagger: 0.1,
          scrollTrigger: {
            trigger: occasionCards[0].closest('section') || occasionCards[0],
            start: 'top 80%',
            once: true
          }
        }
      );
    }
  }

})();
